import React, { createContext, useContext, useMemo, useState } from 'react';

/**
 * Carries the accessible base address of the on-prem device whose interlink-ai
 * (connect-ai) backend the SDM is currently streaming from. The testing debugger uses
 * it to build the `/ai/modules/line_crossing/events/inject` URL when the user opts to
 * inject simulated events through the real backend pipeline instead of client-side.
 *
 * The address is only known deep in the monitor tree (Grid, where the device is
 * resolved) while the consumer (the debugger) is a sibling of the monitor, so it is
 * published here rather than threaded through props.
 */
interface InjectionContextValue {
  /** Device base address (e.g. https://device.local), or null when unknown. */
  injectionBaseUrl: string | null;
  setInjectionBaseUrl: (url: string | null) => void;
}

const ServiceDisplayInjectionContext = createContext<InjectionContextValue | null>(null);

export function ServiceDisplayInjectionProvider({ children }: { children: React.ReactNode }) {
  const [injectionBaseUrl, setInjectionBaseUrl] = useState<string | null>(null);

  const value = useMemo(() => ({ injectionBaseUrl, setInjectionBaseUrl }), [injectionBaseUrl]);

  return (
    <ServiceDisplayInjectionContext.Provider value={value}>
      {children}
    </ServiceDisplayInjectionContext.Provider>
  );
}

/**
 * Returns the injection context. The provider (in Page.tsx) wraps the entire SDM page —
 * both the monitor and the debugger — so it is always present; we throw rather than
 * return null to keep callers free of nullish guards.
 */
export function useServiceDisplayInjection(): InjectionContextValue {
  const value = useContext(ServiceDisplayInjectionContext);
  if (value === null) {
    throw new Error(
      'useServiceDisplayInjection must be used within a ServiceDisplayInjectionProvider',
    );
  }
  return value;
}
